import { useEffect } from 'react';

import useUser from 'src/hooks/use-user';
import { useExclusiveMenuGateModal } from 'src/components/modals/exclusive-menu-gate';

type UseExclusiveCollectionGateProps = {
  isExclusive?: boolean;
  isAccessibleToCurrentUser?: boolean;
  loading: boolean;
};

/**
 * Hook to show the exclusive collection gate modal when appropriate.
 * Opens the modal if the collection is exclusive and the user either:
 * - Is not logged in, OR
 * - Is logged in but doesn't have access
 *
 * Closes the modal when the user gains access (e.g., after logging in).
 */
export function useExclusiveCollectionGate({
  isExclusive,
  isAccessibleToCurrentUser,
  loading,
}: UseExclusiveCollectionGateProps): void {
  const { isLoggedIn } = useUser();
  const { open, close, isOpen } = useExclusiveMenuGateModal();

  // Close modal when user gains access
  useEffect(() => {
    if (isOpen && isLoggedIn && isAccessibleToCurrentUser) {
      close();
    }
  }, [isOpen, isLoggedIn, isAccessibleToCurrentUser, close]);

  useEffect(() => {
    if (loading || !isExclusive) {
      return;
    }

    if (isAccessibleToCurrentUser === undefined || (isLoggedIn && isAccessibleToCurrentUser)) {
      return;
    }

    void open({
      isAccessibleToCurrentUser,
      isLoggedIn,
    });
  }, [loading, isExclusive, isLoggedIn, isAccessibleToCurrentUser, open]);
}
